import { useState } from 'react';
import { createRoute, redirect, useNavigate, useParams } from '@tanstack/react-router';
import { Route as appRoute } from '../../app.js';
import { useAuthStore } from '../../../lib/auth.js';
import { useQuote, type QuoteLineView } from '../../../hooks/useQuotes.js';
import { QuoteSendModal } from '../../../components/quotes/QuoteSendModal.js';
import { activeScholarship, scholarshipLabel } from '../../../lib/pricing.js';
import { centsToAudDisplay } from '../../../lib/money.js';
import { formatDate } from '../../../lib/format.js';
import { editorLabel } from '../../../lib/editor-label.js';
import {
  canBuildQuotes,
  formatQuoteDate,
  visaSubclassDisplay,
  QUOTE_IMMUTABLE_NOTE,
} from '../../../lib/quotes.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/quotes/$quoteId',
  beforeLoad: () => {
    if (!canBuildQuotes(useAuthStore.getState().user)) throw redirect({ to: '/app' });
  },
  component: QuoteDetailRoute,
});

/** The navigation seam — unexported, never rendered by a test. */
function QuoteDetailRoute() {
  // The standalone import + `from`, the courses/course-detail precedent — not
  // Route.useParams(), which only the top-level $token routes use.
  const { quoteId } = useParams({ from: '/app/quotes/$quoteId' });
  const navigate = useNavigate();
  return <QuoteDetailPage quoteId={quoteId} onBack={() => navigate({ to: '/app/quotes' })} />;
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted">{label}</dt>
      <dd className="text-sm text-ink">{value}</dd>
    </div>
  );
}

function LineRow({ line: l }: { line: QuoteLineView }) {
  // activeScholarship takes a loose `kind: string`, so the quote's extra
  // 'not_set' falls through to null → "—". Both 'none' (removed) and 'not_set'
  // (never had one) mean no discount in force, which is what a client-facing
  // document should say. scholarshipVersionLabel would print "Removed" for
  // 'none' and mishandle 'not_set' — never use it here.
  const sch = activeScholarship({
    kind: l.scholarshipKind,
    percentBp: l.scholarshipPercentBp,
    amountCents: l.scholarshipAmountCents,
  });

  return (
    <tr className="align-top">
      <td className="px-5 py-3 text-sm text-muted">{l.lineOrder}</td>
      <td className="px-5 py-3 min-w-[16rem]">
        <span className="block text-sm text-ink">{l.courseName}</span>
        <span className="block text-xs text-muted">{l.providerName}</span>
        {l.cricosCode && <span className="block text-xs text-muted">CRICOS {l.cricosCode}</span>}
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap text-sm text-navy">
        {centsToAudDisplay(l.feeCents)}
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap text-sm text-ink">
        {sch ? scholarshipLabel(sch) : '—'}
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap text-sm font-medium text-navy">
        {centsToAudDisplay(l.netFeeCents)}
      </td>
    </tr>
  );
}

/**
 * One issued quote — the locked, permanent record (Brief 7b).
 *
 * Every figure is the value the server froze on the quote's date; nothing here is
 * recomputed, re-resolved or refetched from the pricing engine. There is no edit
 * and no delete affordance anywhere, because there is no endpoint for either.
 */
export function QuoteDetailPage({
  quoteId,
  onBack,
}: {
  quoteId: string;
  onBack: () => void;
}) {
  const { data, isLoading, error, refetch } = useQuote(quoteId);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [sendOpen, setSendOpen] = useState(false);
  const quote = data?.quote;

  return (
    <div className="max-w-4xl">
      <button className="mb-4 text-sm text-muted underline" onClick={onBack}>
        Back to quotes
      </button>

      {isLoading && <p className="px-1 py-6 text-sm text-muted">Loading quote…</p>}
      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
          Couldn&apos;t load the quote: {(error as Error).message}{' '}
          <button className="underline" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      )}
      {!isLoading && !error && !quote && (
        <p className="px-1 py-6 text-sm text-muted">Quote not found.</p>
      )}

      {!isLoading && !error && quote && (
        <>
          <div className="mb-2 text-xs uppercase tracking-[0.14em] text-muted font-semibold">
            Quote
          </div>
          <div className="flex items-start justify-between gap-4 mb-2">
            <h1 className="font-serif text-4xl tracking-tighter2">{quote.quoteNumber}</h1>
            <button
              type="button"
              onClick={() => setSendOpen(true)}
              className="shrink-0 rounded-md px-4 py-2 text-sm font-medium text-navy"
              style={{ backgroundColor: '#C8A24B' }}
            >
              Send to client
            </button>
          </div>
          <p className="text-muted text-sm mb-6">{QUOTE_IMMUTABLE_NOTE}</p>

          <QuoteSendModal
            open={sendOpen}
            onClose={() => setSendOpen(false)}
            quoteId={quote.id}
            quoteNumber={quote.quoteNumber}
          />

          <dl className="surface p-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
            {/* A calendar date — formatQuoteDate, not formatDate. */}
            <Field label="Quote date" value={formatQuoteDate(quote.quoteDate)} />
            <Field label="Issued by" value={editorLabel(quote, currentUserId)} />
            {/* createdAt IS a real instant — the one place formatDate is right. */}
            <Field label="Created" value={formatDate(quote.createdAt)} />
          </dl>

          <h2 className="font-serif text-xl text-ink mt-8 mb-2">Client</h2>
          <dl className="surface p-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field label="Name" value={quote.client.name} />
            <Field label="Email" value={quote.client.email} />
            <Field label="Phone" value={quote.client.phone ?? '—'} />
            <Field label="Visa" value={visaSubclassDisplay(quote.client.visaSubclass)} />
            <Field label="Nationality" value={quote.client.nationality ?? '—'} />
          </dl>

          <h2 className="font-serif text-xl text-ink mt-8 mb-2">Courses</h2>
          <div className="surface p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream/60 border-b border-line">
                <tr className="text-left text-muted text-xs uppercase tracking-wider">
                  <th className="px-5 py-3 font-medium">#</th>
                  <th className="px-5 py-3 font-medium">Course</th>
                  <th className="px-5 py-3 font-medium text-right">Fee</th>
                  <th className="px-5 py-3 font-medium text-right">Scholarship</th>
                  <th className="px-5 py-3 font-medium text-right">Net fee</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {quote.lines.map((l) => (
                  <LineRow key={l.id} line={l} />
                ))}
              </tbody>
              <tfoot className="bg-cream/60 border-t border-line">
                <tr>
                  <td className="px-5 py-3 text-sm text-ink" colSpan={4}>
                    Total (net)
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap text-sm font-medium text-navy">
                    {/* The SERVER's total, rendered verbatim — never a browser-side
                        sum of the lines. */}
                    {centsToAudDisplay(quote.totalNetFeeCents)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {quote.notes && (
            <>
              <h2 className="font-serif text-xl text-ink mt-8 mb-2">Notes</h2>
              <p className="surface p-5 whitespace-pre-wrap text-sm text-ink-soft">{quote.notes}</p>
            </>
          )}
        </>
      )}
    </div>
  );
}
